import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTreatXAdminAuth } from '../../context/TreatXAdminAuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Slider } from '../../components/ui/slider';
import { Input } from '../../components/ui/input';
import {
  ArrowLeft, Shield, ShieldCheck, Award, Clock, Users,
  RefreshCw, Loader2, ChevronDown, ChevronUp, X, Activity,
  Plane, TrendingUp, Save, AlertTriangle, CheckCircle2
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/Toaster';
import api from '../../utils/api';

const getToken = () => localStorage.getItem('treatx_admin_token') || '';

// ── Score Bar ────────────────────────────────────────────────
const ScoreBar = ({ score, max = 100, color = 'emerald' }) => {
  const pct = Math.min((score / max) * 100, 100);
  const colors = {
    emerald: 'bg-emerald-500',
    blue: 'bg-blue-500',
    amber: 'bg-amber-500',
    slate: 'bg-slate-400',
  };
  return (
    <div className="flex items-center gap-2 flex-1">
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${colors[color] || colors.emerald} transition-all duration-500`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs font-mono text-slate-500 w-8 text-right">{score ?? '-'}</span>
    </div>
  );
};

// ── Score Breakdown Modal ────────────────────────────────────
const BreakdownModal = ({ clinic, onClose }) => {
  if (!clinic) return null;
  const bd = clinic.breakdown || {};
  const rows = [
    { label: 'Accreditations', value: bd.accreditations, max: 25, icon: Award, color: 'emerald' },
    { label: 'Years of Experience', value: bd.years_of_experience, max: 20, icon: Clock, color: 'blue' },
    { label: 'Procedures Performed', value: bd.procedures_performed, max: 20, icon: Activity, color: 'emerald' },
    { label: 'Intl Patient Volume', value: bd.international_patient_volume, max: 10, icon: Plane, color: 'blue' },
    { label: 'Response Speed', value: bd.response_speed, max: 15, icon: TrendingUp, color: 'amber' },
    { label: 'Offer Acceptance Rate', value: bd.offer_acceptance_rate, max: 10, icon: CheckCircle2, color: 'emerald' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose} data-testid="breakdown-modal-overlay">
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="breakdown-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b bg-gradient-to-r from-emerald-50 to-teal-50">
          <div>
            <h2 className="text-sm font-semibold text-[#122033] font-[Manrope]" data-testid="breakdown-clinic-name">{clinic.clinic_name}</h2>
            <p className="text-[10px] text-slate-500 mt-0.5">Trust Score Breakdown</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="bg-emerald-600 text-white text-lg font-bold rounded-full h-10 w-10 flex items-center justify-center" data-testid="breakdown-total-score">
              {clinic.trust_score ?? '-'}
            </div>
            <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-md" data-testid="breakdown-close-btn">
              <X className="h-4 w-4 text-slate-400" />
            </button>
          </div>
        </div>
        <div className="px-5 py-4 space-y-3" data-testid="breakdown-rows">
          {rows.map(({ label, value, max, icon: Icon, color }) => (
            <div key={label} className="flex items-center gap-3" data-testid={`breakdown-row-${label.toLowerCase().replace(/\s+/g, '-')}`}>
              <Icon className="h-4 w-4 text-slate-400 shrink-0" />
              <span className="text-xs text-slate-600 w-36 shrink-0">{label}</span>
              <ScoreBar score={value != null ? Math.round(value * 10) / 10 : 0} max={max} color={color} />
              <span className="text-[10px] text-slate-400 w-8 text-right">/{max}</span>
            </div>
          ))}
        </div>
        <div className="px-5 py-3 border-t bg-slate-50 flex items-center gap-2 text-[10px] text-slate-400">
          <Shield className="h-3 w-3" />
          Raw inputs are admin-only. Patients see only trust badges.
        </div>
      </div>
    </div>
  );
};

// ── Clinic Row ───────────────────────────────────────────────
const ClinicRow = ({ clinic, rank, onSelect }) => {
  const isTop = rank <= 3;
  const isVerified = clinic.verification_level === 'verified';
  const speedLabel = clinic.response_speed_hours != null
    ? clinic.response_speed_hours < 1 ? 'Fast' : clinic.response_speed_hours < 6 ? 'Quick' : clinic.response_speed_hours < 24 ? 'Same day' : '> 24h'
    : '-';

  return (
    <tr
      className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${isTop ? 'bg-emerald-50/30' : ''}`}
      onClick={() => onSelect(clinic)}
      data-testid={`clinic-row-${clinic.clinic_id}`}
    >
      <td className="px-3 py-2.5">
        <span className={`text-xs font-bold ${isTop ? 'text-emerald-600' : 'text-slate-400'}`} data-testid={`rank-${clinic.clinic_id}`}>
          #{rank}
        </span>
      </td>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-800" data-testid={`name-${clinic.clinic_id}`}>{clinic.clinic_name}</span>
          {isVerified && (
            <Badge className="text-[9px] bg-emerald-100 text-emerald-700 border-emerald-200 gap-0.5" data-testid={`verified-badge-${clinic.clinic_id}`}>
              <ShieldCheck className="h-2.5 w-2.5" /> Verified
            </Badge>
          )}
        </div>
      </td>
      <td className="px-3 py-2.5 text-center">
        <span className={`text-sm font-bold ${(clinic.trust_score ?? 0) >= 70 ? 'text-emerald-600' : (clinic.trust_score ?? 0) >= 40 ? 'text-amber-600' : 'text-slate-400'}`}
          data-testid={`score-${clinic.clinic_id}`}>
          {clinic.trust_score ?? '-'}
        </span>
      </td>
      <td className="px-3 py-2.5">
        <div className="flex flex-wrap gap-0.5">
          {(clinic.verified_accreditations_canonical || []).map((a, i) => (
            <Badge key={i} className="text-[8px] bg-blue-50 text-blue-600 border-blue-100" data-testid={`acc-${a}-${clinic.clinic_id}`}>{a}</Badge>
          ))}
          {!(clinic.verified_accreditations_canonical?.length) && <span className="text-[10px] text-slate-300">None</span>}
        </div>
      </td>
      <td className="px-3 py-2.5 text-center text-xs text-slate-600" data-testid={`years-${clinic.clinic_id}`}>{clinic.years_of_experience ?? '-'}</td>
      <td className="px-3 py-2.5 text-center text-xs text-slate-600" data-testid={`procs-${clinic.clinic_id}`}>
        {clinic.procedures_performed != null ? clinic.procedures_performed.toLocaleString() : '-'}
      </td>
      <td className="px-3 py-2.5 text-center text-xs text-slate-600" data-testid={`intl-${clinic.clinic_id}`}>
        {clinic.international_patient_volume != null ? clinic.international_patient_volume.toLocaleString() : '-'}
      </td>
      <td className="px-3 py-2.5 text-center">
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
          speedLabel === 'Fast' ? 'bg-emerald-100 text-emerald-700' :
          speedLabel === 'Quick' ? 'bg-blue-100 text-blue-700' :
          speedLabel === 'Same day' ? 'bg-amber-100 text-amber-700' :
          'bg-slate-100 text-slate-500'
        }`} data-testid={`speed-${clinic.clinic_id}`}>{speedLabel}</span>
      </td>
      <td className="px-3 py-2.5 text-center text-xs text-slate-600" data-testid={`acceptance-${clinic.clinic_id}`}>
        {clinic.offer_acceptance_rate != null ? `${Math.round(clinic.offer_acceptance_rate)}%` : '-'}
      </td>
    </tr>
  );
};

// ── Main Dashboard ───────────────────────────────────────────
export default function AdminTrustScoreDashboard() {
  const { admin } = useTreatXAdminAuth();
  const navigate = useNavigate();

  const [config, setConfig] = useState({ trust_score_weight: 0, enabled: false });
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [sortField, setSortField] = useState('trust_score');
  const [sortDir, setSortDir] = useState('desc');
  const [localWeight, setLocalWeight] = useState(0);
  const [weightDirty, setWeightDirty] = useState(false);
  const [showRecomputeConfirm, setShowRecomputeConfirm] = useState(false);

  const authHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
  }), []);

  // ── Fetch data ─────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, clinicsRes] = await Promise.all([
        api.get(`/admin/trust-score/config`),
        api.get(`/admin/trust-score/clinics`),
      ]);
      const configData = configRes.data;
      const clinicsData = clinicsRes.data;

      if (configData.success) {
        setConfig(configData);
        setLocalWeight(configData.trust_score_weight);
      }
      if (clinicsData.success) {
        setClinics(clinicsData.clinics || []);
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load trust score data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Save weight ────────────────────────────────────────────
  const handleSaveWeight = async () => {
    setSaving(true);
    try {
      const res = await api.post('/admin/trust-score/config', {
        trust_score_weight: config?.trust_score_weight,
      });
      const data = res.data;
      if (data.success) {
        setConfig(data);
        setWeightDirty(false);
        toast({ title: 'Saved', description: `Trust score weight set to ${data.trust_score_weight}` });
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to update weight', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // ── Recompute ──────────────────────────────────────────────
  const handleRecompute = async () => {
    setShowRecomputeConfirm(false);
    setRecomputing(true);
    try {
      const res = await api.post(`/admin/trust-score/recompute`);
      const data = res.data;
      if (data.success) {
        toast({ title: 'Recomputed', description: `Updated ${data.clinics_updated} clinic scores` });
        await fetchData();
      }
    } catch {
      toast({ title: 'Error', description: 'Recompute failed', variant: 'destructive' });
    } finally {
      setRecomputing(false);
    }
  };

  // ── Sorting ────────────────────────────────────────────────
  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sorted = [...clinics].sort((a, b) => {
    const av = a[sortField] ?? -1;
    const bv = b[sortField] ?? -1;
    return sortDir === 'desc' ? bv - av : av - bv;
  });

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDown className="h-3 w-3 text-slate-300" />;
    return sortDir === 'desc'
      ? <ChevronDown className="h-3 w-3 text-emerald-600" />
      : <ChevronUp className="h-3 w-3 text-emerald-600" />;
  };

  const verifiedCount = clinics.filter(c => c.verification_level === 'verified').length;
  const avgScore = clinics.length
    ? Math.round(clinics.reduce((s, c) => s + (c.trust_score || 0), 0) / clinics.length)
    : 0;

  return (
    <div className="min-h-screen bg-[#f8f9fb]" data-testid="admin-trust-score-dashboard">
      <Toaster />
      {selectedClinic && <BreakdownModal clinic={selectedClinic} onClose={() => setSelectedClinic(null)} />}

      {/* Recompute confirm dialog */}
      {showRecomputeConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" data-testid="recompute-confirm-overlay">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <h3 className="text-sm font-semibold text-[#122033]">Recompute All Trust Scores?</h3>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              This will recalculate trust scores for all verified clinics based on current metrics. Existing stored scores will be overwritten.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setShowRecomputeConfirm(false)} data-testid="recompute-cancel-btn">Cancel</Button>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleRecompute} data-testid="recompute-confirm-btn">
                <RefreshCw className="h-3 w-3 mr-1" /> Recompute
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/treatx-admin/dashboard')} className="p-1.5 hover:bg-slate-100 rounded-md" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 text-slate-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-base font-bold text-[#122033] font-[Manrope]" data-testid="page-title">Trust Score Dashboard</h1>
            <p className="text-[10px] text-slate-400">Configure ranking influence and monitor clinic trust scores</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowRecomputeConfirm(true)}
            disabled={recomputing}
            data-testid="recompute-btn"
          >
            {recomputing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
            Recompute All
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 space-y-5">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
          </div>
        ) : (
          <>
            {/* Section 1: Global Config */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="md:col-span-2 border border-emerald-200/60" data-testid="weight-config-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-[#122033] flex items-center gap-2">
                    <Shield className="h-4 w-4 text-emerald-600" />
                    Trust Score Weight
                  </CardTitle>
                  <p className="text-[10px] text-slate-400">Controls how much verified trust scores influence clinic ranking. 0 = disabled.</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4">
                    <Slider
                      value={[localWeight]}
                      min={0}
                      max={1}
                      step={0.05}
                      onValueChange={([v]) => { setLocalWeight(v); setWeightDirty(true); }}
                      className="flex-1"
                      data-testid="weight-slider"
                    />
                    <Input
                      type="number"
                      min={0}
                      max={1}
                      step={0.05}
                      value={localWeight}
                      onChange={e => { setLocalWeight(parseFloat(e.target.value) || 0); setWeightDirty(true); }}
                      className="w-20 text-center text-sm"
                      data-testid="weight-input"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-[10px] text-slate-400">
                      <span className="px-2 py-0.5 bg-slate-100 rounded">0.0 = Disabled</span>
                      <span className="px-2 py-0.5 bg-amber-50 text-amber-600 rounded">0.1 = Light</span>
                      <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded">0.3 = Strong</span>
                    </div>
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      disabled={!weightDirty || saving}
                      onClick={handleSaveWeight}
                      data-testid="weight-save-btn"
                    >
                      {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
                      Save
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Stats */}
              <div className="space-y-4">
                <Card className="border border-slate-200" data-testid="stat-clinics-card">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <ShieldCheck className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-[#122033]" data-testid="stat-verified-count">{verifiedCount}</p>
                      <p className="text-[10px] text-slate-400">Verified Clinics</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border border-slate-200" data-testid="stat-avg-card">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-[#122033]" data-testid="stat-avg-score">{avgScore}</p>
                      <p className="text-[10px] text-slate-400">Avg Trust Score</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Section 2: Clinic Trust Table */}
            <Card className="border border-slate-200" data-testid="clinic-trust-table-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-[#122033] flex items-center gap-2">
                  <Users className="h-4 w-4 text-slate-400" />
                  Clinic Trust Scores
                  <Badge className="text-[9px] bg-slate-100 text-slate-500">{clinics.length} clinics</Badge>
                </CardTitle>
                <p className="text-[10px] text-slate-400">Click a row to see score breakdown. Top 3 highlighted.</p>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-left" data-testid="clinic-trust-table">
                    <thead className="bg-slate-50 border-y border-slate-100">
                      <tr>
                        <th className="px-3 py-2 text-[10px] font-medium text-slate-400 w-10">#</th>
                        <th className="px-3 py-2 text-[10px] font-medium text-slate-400">Clinic</th>
                        <th className="px-3 py-2 text-[10px] font-medium text-slate-400 text-center cursor-pointer" onClick={() => toggleSort('trust_score')} data-testid="sort-score">
                          <span className="inline-flex items-center gap-0.5">Score <SortIcon field="trust_score" /></span>
                        </th>
                        <th className="px-3 py-2 text-[10px] font-medium text-slate-400">Accreditations</th>
                        <th className="px-3 py-2 text-[10px] font-medium text-slate-400 text-center cursor-pointer" onClick={() => toggleSort('years_of_experience')} data-testid="sort-years">
                          <span className="inline-flex items-center gap-0.5">Years <SortIcon field="years_of_experience" /></span>
                        </th>
                        <th className="px-3 py-2 text-[10px] font-medium text-slate-400 text-center cursor-pointer" onClick={() => toggleSort('procedures_performed')} data-testid="sort-procedures">
                          <span className="inline-flex items-center gap-0.5">Procedures <SortIcon field="procedures_performed" /></span>
                        </th>
                        <th className="px-3 py-2 text-[10px] font-medium text-slate-400 text-center cursor-pointer" onClick={() => toggleSort('international_patient_volume')} data-testid="sort-intl">
                          <span className="inline-flex items-center gap-0.5">Intl Vol <SortIcon field="international_patient_volume" /></span>
                        </th>
                        <th className="px-3 py-2 text-[10px] font-medium text-slate-400 text-center">Speed</th>
                        <th className="px-3 py-2 text-[10px] font-medium text-slate-400 text-center cursor-pointer" onClick={() => toggleSort('offer_acceptance_rate')} data-testid="sort-acceptance">
                          <span className="inline-flex items-center gap-0.5">Accept <SortIcon field="offer_acceptance_rate" /></span>
                        </th>
                      </tr>
                    </thead>
                    <tbody data-testid="clinic-rows">
                      {sorted.map((c, i) => (
                        <ClinicRow key={c.clinic_id} clinic={c} rank={i + 1} onSelect={setSelectedClinic} />
                      ))}
                      {!sorted.length && (
                        <tr>
                          <td colSpan={9} className="px-3 py-8 text-center text-sm text-slate-400">
                            No clinics with trust metrics
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Status badge */}
            <div className="flex items-center gap-2 text-[10px] text-slate-400 justify-center pb-4">
              <Shield className="h-3 w-3" />
              <span>Status: weight={config.trust_score_weight} ({config.enabled ? 'Enabled' : 'Disabled'})</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
